<template>
  <div class="wrapper_group_dashboard">
    <div class="group_dashboard">
      <div class="all_invoices">
        <breadcrumb :items="breadcrumbItems" class="invoice-detail-breadcrumb" />
        <div class="title">
          <h2>Group Invoice Details</h2>
        </div>

        <p v-if="errorMessage" class="text-danger mg_message">{{ errorMessage }}</p>

        <template v-else-if="loaded">
          <div v-if="groupInfo" class="mg_banner">
            <p class="mg_banner_title">
              Viewing member invoices of <strong>{{ groupInfo.name }}</strong> for {{ periodLabel }}
            </p>
            <ul v-if="groupContact.length" class="mg_banner_contact">
              <li v-for="(item, index) in groupContact" :key="index">{{ item }}</li>
            </ul>
          </div>

          <div class="mg_summary">
            <div class="mg_stat">
              <span class="mg_stat_label">Total Policies</span>
              <span class="mg_stat_value">{{ summary.policy_count }}</span>
            </div>
            <div class="mg_stat">
              <span class="mg_stat_label">Paid / Unpaid</span>
              <span class="mg_stat_value">{{ summary.paid_policy_count }} / {{ summary.unpaid_policy_count }}</span>
              <span v-if="summary.termed_policy_count > 0" class="mg_stat_note">
                Termed: {{ summary.termed_policy_count }}
              </span>
            </div>
            <div class="mg_stat">
              <span class="mg_stat_label">Invoice Total</span>
              <span class="mg_stat_value">{{ summary.invoice_total | currency }}</span>
            </div>
            <div class="mg_stat">
              <span class="mg_stat_label">Enrollment Fee</span>
              <span class="mg_stat_value">{{ summary.invoice_efee | currency }}</span>
            </div>
            <div class="mg_stat">
              <span class="mg_stat_label">Previous Due</span>
              <span class="mg_stat_value">{{ summary.invoice_prev_due | currency }}</span>
            </div>
            <div class="mg_stat">
              <span class="mg_stat_label">Paid Amount</span>
              <span class="mg_stat_value">{{ summary.invoice_due_amount_paid | currency }}</span>
            </div>
            <div class="mg_stat mg_stat_due">
              <span class="mg_stat_label">Due Amount</span>
              <span class="mg_stat_value">{{ summary.invoice_due_amount | currency }}</span>
            </div>
          </div>

          <div class="mg_filters">
            <select v-model="statusFilter" class="form-control">
              <option v-for="status in statusOptions" :key="status" :value="status">{{ status }}</option>
            </select>
            <input v-model="search" type="text" class="form-control" placeholder="Search" />
          </div>

          <div class="table_container" style="overflow-x: auto">
            <table>
              <thead>
                <tr>
                  <td>Inv #</td>
                  <td>Member Detail</td>
                  <td>Dates | Terms</td>
                  <td>Totals</td>
                  <td>Status</td>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in filteredInvoices" :key="item.invoice_id">
                  <td>
                    <strong>{{ item.invoice_number }}</strong>
                    <br />
                    System ID: {{ item.policy_id }}
                    <span v-if="item.term_status === 'TERMED'">
                      <br />
                      TERMED [{{ item.term_date | date_format("YYYY-MM-DD", "MM/DD/YYYY") }}]
                    </span>
                  </td>
                  <td>
                    <a
                      v-if="clientDetailsUrl(item)"
                      :href="clientDetailsUrl(item)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="mg_client_link link-hover"
                      v-tooltip="'Open client details'"
                    >
                      <strong class="mg_member_name">{{ memberLabel(item) }}</strong>
                      <span class="mg_link_icon"><i class="fas fa-external-link-alt"></i></span>
                    </a>
                    <strong v-else class="mg_member_name">{{ item.member_name }}</strong>
                    <span v-if="item.address1">
                      <br />
                      {{ item.address1 }}
                    </span>
                    <span v-if="item.city || item.state || item.zip">
                      <br />
                      {{ [item.city, item.state, item.zip].filter(Boolean).join(", ") }}
                    </span>
                  </td>
                  <td>
                    Issue Date: {{ item.invoice_date | date_format("YYYY-MM-DD", "MM/DD/YYYY") }}
                    <br />
                    Due Date: {{ item.invoice_due_date | date_format("YYYY-MM-DD", "MM/DD/YYYY") }}
                    <br />
                    From Date: {{ item.invoice_start_date | date_format("YYYY-MM-DD", "MM/DD/YYYY") }}
                    <br />
                    To Date: {{ item.invoice_end_date | date_format("YYYY-MM-DD", "MM/DD/YYYY") }}
                  </td>
                  <td>
                    Total: {{ item.invoice_total | currency }}
                    <br />
                    Enrollment Fee: {{ item.invoice_efee | currency }}
                    <span v-if="item.invoice_late_fee > 0">
                      <br />
                      Late Fee: {{ item.invoice_late_fee | currency }}
                    </span>
                    <span v-if="item.invoice_bounce_fee > 0">
                      <br />
                      Bounce Fee: {{ item.invoice_bounce_fee | currency }}
                    </span>
                    <span v-if="item.invoice_stmt_fee > 0">
                      <br />
                      Statement Fee: {{ item.invoice_stmt_fee | currency }}
                    </span>
                    <br />
                    Previous Due: {{ item.invoice_prev_due | currency }}
                    <br />
                    Due: {{ item.invoice_due_amount | currency }}
                    <br />
                    Paid: {{ item.invoice_due_amount_paid | currency }}
                    <span v-if="isPaidOrPartial(item)">
                      <br />
                      Balance: {{ overallDue(item) | currency }}
                    </span>
                  </td>
                  <td>
                    {{ item.invoice_payment_status }}
                    <span v-if="item.payment_party_status === 'PROCESSING'">
                      <br />
                      PROCESSING
                    </span>
                    <span v-if="item.processing_date">
                      <br />
                      Scheduled for {{ item.processing_date | date_format("YYYY-MM-DD", "MM/DD/YYYY") }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
            <no-content v-if="!filteredInvoices.length" text="No invoices found." />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
<script>
import Api from "@/resource/Api";
import moment from "moment";

export default {
  name: "MultiGroupSubGroupInvoiceDetails",
  data() {
    return {
      groupInfo: null,
      summary: {},
      invoices: [],
      loaded: false,
      errorMessage: "",
      statusFilter: "ALL",
      search: "",
      // Same options as Commission Billing's ListInvoicesOfGroup "Invoice Status" filter.
      statusOptions: ["ALL", "UNPAID", "PAID", "PARTIAL", "LATE", "TERM", "VOID"],
    };
  },
  computed: {
    // Admin site hosting /ClientsDetail/{policyId}; falls back to the host of the invoice API (same site).
    clientDetailsBaseUrl() {
      const configured = process.env.VUE_APP_ADMIN_DASHBOARD_URL;
      if (configured) {
        return configured.replace(/\/+$/, "");
      }
      try {
        return new URL(process.env.VUE_APP_API_INVOICE_URL).origin;
      } catch (e) {
        return "";
      }
    },
    subGroupId() {
      return this.$route.params.subGroupId;
    },
    invoiceDate() {
      return this.$route.params.date;
    },
    periodLabel() {
      return moment(this.invoiceDate, "YYYY-MM-DD").format("MMMM YYYY");
    },
    breadcrumbItems() {
      // Group Invoices is keyed by the first of the month; this page receives the invoice end date.
      const date = moment(this.invoiceDate, "YYYY-MM-DD", true);
      const groupInvoices = date.isValid()
        ? { name: "MultiGroupGroupInvoices", params: { date: date.startOf("month").format("YYYY-MM-DD") } }
        : null;
      return [
        { title: "Group Invoices", path: groupInvoices },
        { title: "Group Invoice Details", active: true },
      ];
    },
    groupContact() {
      if (!this.groupInfo) {
        return [];
      }
      const { address1, address2, city, state, zip, email, phone } = this.groupInfo;
      const address = [address1, address2, city, [state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
      return [address, email, phone].filter(Boolean);
    },
    filteredInvoices() {
      const search = this.search.trim().toLowerCase();
      return this.invoices.filter((item) => {
        // Commission Billing filters with a "^STATUS" prefix match on the status column.
        if (this.statusFilter !== "ALL" && !(item.invoice_payment_status || "").startsWith(this.statusFilter)) {
          return false;
        }
        if (!search) {
          return true;
        }
        return [
          item.invoice_number,
          item.policy_id,
          item.member_name,
          item.address1,
          item.city,
          item.state,
          item.zip,
        ].some((value) => value !== null && value !== undefined && String(value).toLowerCase().includes(search));
      });
    },
  },
  methods: {
    // System ID is the client's policy id.
    clientDetailsUrl(item) {
      if (!this.clientDetailsBaseUrl || !item.policy_id) {
        return null;
      }
      return `${this.clientDetailsBaseUrl}/ClientsDetail/${encodeURIComponent(item.policy_id)}`;
    },
    memberLabel(item) {
      return item.member_name || `System ID: ${item.policy_id}`;
    },
    isPaidOrPartial(item) {
      return item.invoice_payment_status === "PAID" || item.invoice_payment_status === "PARTIAL";
    },
    overallDue(item) {
      return (item.invoice_due_amount - item.invoice_due_amount_paid).toFixed(2);
    },
    getInvoiceDetails() {
      const validLink = /^[0-9]+$/.test(this.subGroupId || "") && moment(this.invoiceDate, "YYYY-MM-DD", true).isValid();
      if (!validLink) {
        this.errorMessage = "This invoice link is not valid.";
        return;
      }

      let loader = this.$loading.show();
      Api.get(`multigroup-invoice/group-invoices/${this.subGroupId}/${this.invoiceDate}/details`)
        .then((res) => {
          this.groupInfo = res.data.data.group_info;
          this.summary = res.data.data.summary || {};
          this.invoices = res.data.data.invoices || [];
          this.loaded = true;
        })
        .catch((err) => {
          if (err && err.response && err.response.status === 403) {
            this.errorMessage = "You are not authorized to view invoices of this group.";
            return;
          }
          this.errorMessage = "Unable to load invoice details. Please try again later.";
          this.$notify(this.errorMessage, "error");
        })
        .finally(() => {
          loader.hide();
        });
    },
  },
  created() {
    this.getInvoiceDetails();
  },
};
</script>
<style src="@/assets/css/group_dashboard/all_invoices.css"></style>
<style scoped>
/* Banner, summary cards and filters mirror MultiGroupGroupInvoices. */
.mg_banner {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 15px;
  color: #254050;
}
.mg_banner p {
  margin: 0;
}
.mg_banner_title {
  font-size: 15px;
  line-height: 1.5;
}
.mg_banner_contact {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  row-gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 13px;
  line-height: 1.5;
  letter-spacing: 0.2px;
  color: #778699;
}
.mg_banner_contact li {
  display: flex;
  align-items: center;
}
.mg_banner_contact li + li::before {
  content: "";
  display: inline-block;
  width: 1px;
  height: 12px;
  margin: 0 12px;
  background: #c5ced8;
}
.mg_summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-top: 15px;
}
.mg_stat {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  background: #fff;
  border-radius: 10px;
  box-shadow: -2px 2px 5px #43566420;
}
.mg_stat_label {
  font-size: 12px;
  color: #778699;
}
.mg_stat_value {
  font-size: 18px;
  font-weight: 600;
  color: #254050;
}
.mg_stat_note {
  font-size: 12px;
  color: #d0283e;
}
.mg_stat_due {
  border-left: 3px solid #03c3ec;
}
.mg_stat_due .mg_stat_value {
  color: #0396b6;
}
.mg_filters {
  display: flex;
  gap: 10px;
  margin-top: 20px;
  max-width: 500px;
}
.mg_member_name {
  text-transform: uppercase;
}
/* Same look as the external links on the ticketing pages (primary colour + external-link icon). */
.mg_client_link,
.mg_client_link:hover {
  color: #03c3ec;
}
.mg_link_icon {
  margin-left: 5px;
  font-size: 12px;
}
.mg_message {
  margin-top: 20px;
}
</style>
